import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PDFViewer from '../components/PDFViewer';
import OTPVerification from '../components/OTPVerification';
import SignatureConsent from '../components/SignatureConsent';
import LoadingSpinner from '../components/LoadingSpinner';
import { useSignatureStore } from '../store/signatureStore';
import { api } from '../services/api';
import type { SignatureChoice } from '../components/SignaturePad';

type Factor = 'email' | 'sms';

type Step = 'loading' | 'viewing' | 'otp' | 'consent' | 'signing' | 'complete' | 'done';

function SignaturePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('loading');
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [factors, setFactors] = useState<{ required: Factor[]; pending: Factor[] }>({ required: [], pending: [] });

  const {
    document,
    signer,
    signatureField,
    branding,
    loadSession,
    markViewed,
    signDocument
  } = useSignatureStore();

  useEffect(() => {
    if (token) {
      loadSessionData(token);
    }
  }, [token]);

  const loadSessionData = async (sessionToken: string) => {
    try {
      await loadSession(sessionToken);
      // Lien rouvert apres la signature : plus de page de signature, seulement le document signe
      const { signer: loaded } = useSignatureStore.getState();
      setStep(loaded?.status === 'signed' ? 'done' : 'viewing');
    } catch (err: any) {
      setError(err.message || 'Session invalide ou expirée');
      navigate('/error');
    }
  };

  const handleDocumentViewed = async () => {
    try {
      const progress = await markViewed();
      setFactors({ required: progress.required, pending: progress.pending });
      // Le lien reçu sur son canal suffit : pas de code à saisir, on passe directement à la signature
      setStep(progress.authenticated ? 'consent' : 'otp');
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleOTPVerified = async () => {
    setStep('consent');
  };

  const handleConsent = async (signature?: SignatureChoice | null) => {
    setStep('signing');
    try {
      const { allSigned } = await signDocument(signature);
      setStep('complete');

      // Redirect to success URL if provided
      if (document?.return_url_success) {
        window.location.href = document.return_url_success;
      } else {
        navigate('/success', { state: { token, title: document?.title, allSigned } });
      }
    } catch (err: any) {
      setError(err.message);
      if (document?.return_url_error) {
        window.location.href = document.return_url_error;
      } else {
        navigate('/error');
      }
    }
  };

  const downloadSigned = async () => {
    setDownloading(true);
    setError(null);
    try {
      const response = await api.post('/session/document/signed', { token }, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = `${document?.title ?? 'document'}_signe.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.response?.status === 404
        ? "Le document signé sera disponible quand tous les signataires auront signé. Vous le recevrez alors par e-mail."
        : 'Impossible de télécharger le document pour le moment.');
    } finally {
      setDownloading(false);
    }
  };

  const handleDecline = () => {
    if (document?.return_url_cancel) {
      window.location.href = document.return_url_cancel;
    } else {
      navigate('/');
    }
  };

  if (step === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" style={{
      ...(branding?.primary_color && { '--primary-color': branding.primary_color } as any)
    }}>
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {branding?.logo_url ? (
              <img src={branding.logo_url} alt="Logo" className="h-10" />
            ) : (
              <div className="flex items-center gap-2.5">
                <img src="/favicon.svg" alt="" className="h-9 w-9" />
                <h1 className="text-2xl font-bold text-gray-900">
                  {branding?.company_name || 'Juriseal'}
                </h1>
              </div>
            )}
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">{signer?.full_name}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {step === 'done' && document && (
          <div className="max-w-md mx-auto card text-center">
            <img src="/img/success.webp" alt="" className="mx-auto mb-2 h-40 w-40 rounded-3xl object-contain" />
            <h2 className="text-xl font-semibold mb-2">C'est signé, merci {signer?.full_name?.split(' ')[0]} !</h2>
            <p className="text-sm text-gray-500 mb-1">Vous avez déjà signé ce document.</p>
            <p className="text-gray-600 mb-6">« {document.title} »</p>
            <button
              onClick={downloadSigned}
              disabled={downloading}
              className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {downloading ? 'Téléchargement...' : 'Télécharger le document signé'}
            </button>
          </div>
        )}

        {step === 'viewing' && document && (
          <div className="space-y-6">
            <div className="card">
              <p className="text-sm text-gray-500">Bonjour {signer?.full_name?.split(' ')[0]}, voici le document à signer.</p>
              <h2 className="text-xl font-semibold mb-4">{document.title}</h2>
              <PDFViewer
                documentUrl={`/api/documents/${document.id}/preview`}
                onViewed={handleDocumentViewed}
              />
            </div>
          </div>
        )}

        {step === 'otp' && signer && (
          <div className="max-w-md mx-auto">
            <OTPVerification
              signerId={signer.id}
              required={factors.required}
              pending={factors.pending}
              email={signer.email}
              phone={signer.phone}
              onVerified={handleOTPVerified}
              onCancel={handleDecline}
            />
          </div>
        )}

        {step === 'consent' && document && signer && (
          <div className="max-w-2xl mx-auto">
            <SignatureConsent
              document={document}
              signer={signer}
              signatureStyle={document.signature_style ?? 'none'}
              signatureField={signatureField}
              onConsent={handleConsent}
              onDecline={handleDecline}
            />
          </div>
        )}

        {step === 'signing' && (
          <div className="flex flex-col items-center justify-center py-20">
            <LoadingSpinner size="large" />
            <p className="mt-4 text-lg text-gray-600">Signature en cours...</p>
            <p className="mt-2 text-sm text-gray-500">
              Veuillez patienter, cette opération peut prendre quelques instants.
            </p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12">
        <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          <p className="text-center text-sm text-gray-500">
            Signature électronique avancée (AES) conforme au règlement eIDAS
          </p>
        </div>
      </footer>
    </div>
  );
}

export default SignaturePage;
